/**
 * Маска российского номера: +7 (XXX) XXX-XX-XX.
 *
 * `formatPhoneRu(raw)` принимает любую введённую строку (вставка/набор
 * вручную / +7 / 8 / просто цифры) и возвращает отформатированный номер,
 * который безопасно отображать в input.
 *
 * Семантика:
 *  - Если первая цифра «8» — она трактуется как код страны 7 (как в РФ).
 *  - Если первая цифра «7» — она съедается префиксом +7.
 *  - Любые другие цифры подставляются после +7 в скобки.
 *  - Пустая строка отображается как пустая (placeholder покажет «+7 ...»).
 *
 * Возвращает строку до 18 символов (полный формат) — длиннее не имеет смысла.
 */
export function formatPhoneRu(raw: string): string {
  // 1) Оставляем только цифры
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return "";

  // 2) Нормализуем код страны: 7/8 в начале → код 7, иначе предполагаем что
  //    пользователь начал вводить городскую часть номера без кода.
  let rest: string;
  if (digits[0] === "7" || digits[0] === "8") {
    rest = digits.slice(1);
  } else {
    rest = digits;
  }
  rest = rest.slice(0, 10); // максимум 10 цифр после +7

  // 3) Собираем шаблон по мере наполнения
  const p1 = rest.slice(0, 3);  // (XXX)
  const p2 = rest.slice(3, 6);  // XXX
  const p3 = rest.slice(6, 8);  // XX
  const p4 = rest.slice(8, 10); // XX

  let out = "+7";
  if (p1.length) out += ` (${p1}`;
  if (p1.length === 3) out += ")";
  if (p2.length) out += ` ${p2}`;
  if (p3.length) out += `-${p3}`;
  if (p4.length) out += `-${p4}`;
  return out;
}

/**
 * Хелпер для onChange-обработчика input'а телефона.
 *
 * Проблема: когда пользователь нажимает Backspace на разделителе (`)`, `-`,
 * `пробел`), браузер удаляет один символ — но `formatPhoneRu` тут же
 * возвращает его обратно, и Backspace ничего не делает.
 *
 * Решение: если форматирование вернуло то же самое значение, что было до
 * нажатия, и при этом raw короче — значит пользователь стёр разделитель.
 * Снимаем ещё одну цифру с конца и форматируем заново.
 */
export function applyPhoneInput(raw: string, oldValue: string): string {
  const formatted = formatPhoneRu(raw);
  if (formatted === oldValue && raw.length < oldValue.length) {
    const digits = raw.replace(/\D/g, "");
    return formatPhoneRu(digits.slice(0, -1));
  }
  return formatted;
}

/**
 * Чистая E.164 версия для отправки на бэкенд: только цифры с +7.
 * `+71234567890` или пустая строка если нет ни одной цифры.
 */
export function phoneToE164(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const normalized =
    digits[0] === "7" || digits[0] === "8" ? digits.slice(1) : digits;
  return `+7${normalized.slice(0, 10)}`;
}
